import { getStatusBooleanValue, RuleId, ValidPageTitleFailedReason, validPageTitleRule } from '@wix/a11y-rules';
import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { Text } from '@wix/design-system';
import { Trans, useErrorHandler, useExperiments, useHttpClient, useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleTipBox, RuleTipItem, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { TITLE_MAX_LENGTH } from '@wix/seo-validations';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback, useMemo, useState } from 'react';
import { setTitleTag } from '../../api/utils/set-title-tag';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { ruleApplyTriggeredAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { convertScanStatusToUiStatus } from '../../components/fixers/alt-image-fixer/utils';
import { showSaveErrorModal } from '../../components/modals/show-save-error-modal';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { THANK_YOU_PAGE_TPA_ID } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { PanoramaErrorNames, PanoramaTransactions } from '../../panorama/constants';
import { usePanoramaFunctions } from '../../panorama/use-panorama-functions';
import { getFixerDesignPropsFromCapabilities, getFooterButtonTypesFromCapabilities } from '../../utils/get-fixer-design-props';
import { useRuleBase } from '../shared';
import { getPageTypeForBi } from '../shared/bi-helpers';
const ruleChecker = validPageTitleRule.checker;
export const useValidStaticPageTitleRule = function (itemType) {
  if (itemType === void 0) {
    itemType = ITEM_TYPES.STATIC_PAGE_V2;
  }
  const httpClient = useHttpClient();
  const {
    getRuleState,
    getGroupData
  } = usePanelStateAtom();
  const navigateToPage = useNavigation();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const errorHandler = useErrorHandler();
  const biLogger = useBiLogger();
  const {
    experiments
  } = useExperiments();
  const isControlledInputFixerEnabled = experiments.enabled('specs.responsive-editor.EnableControlledInputFixer');
  const isPlaceholderEnabled = experiments.enabled('specs.responsive-editor.EnablePlaceholderForTitleTag');
  const {
    finishPanoramaTransaction,
    startPanoramaTransaction,
    reportErrorPanorama
  } = usePanoramaFunctions();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const [inputValues, setInputValues] = useState({});
  const tip = useMemo(() => {
    return /*#__PURE__*/React.createElement(Text, {
      size: "tiny",
      weight: "thin",
      secondary: true
    }, /*#__PURE__*/React.createElement(Trans, {
      components: {
        bold: /*#__PURE__*/React.createElement(Text, {
          size: "tiny",
          weight: "bold",
          secondary: true
        })
      },
      i18nKey: "accessibility-wizard.rules-list.valid-title-tag.tip.1"
    }));
  }, []);
  const getValidStaticPageTitleProps = useCallback(_ref => {
    var _ruleState$state, _ruleState$state2;
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref;
    const {
      result,
      ruleId
    } = ruleExecutionResult;
    const {
      status,
      title
    } = result;
    const maxLength = TITLE_MAX_LENGTH;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const ruleState = getRuleState(routerId ?? pageId, ruleId);
    if (isOptional(itemType)) {
      throw new Error('itemType is required');
    }
    const uiStatus = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.status) ?? convertScanStatusToUiStatus(status);
    const initialTitle = (ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.pageTitle) ?? title;
    const currentInputValue = inputValues[pageId] ?? initialTitle;
    if (isControlledInputFixerEnabled && !(pageId in inputValues)) {
      setInputValues(prev => ({
        ...prev,
        [pageId]: initialTitle
      }));
    }
    const groupData = getGroupData(pageId);
    const isThankYouPage = (groupData == null ? void 0 : groupData.tpaPageId) === THANK_YOU_PAGE_TPA_ID;
    const handleClickApply = async value => {
      startPanoramaTransaction(PanoramaTransactions.validStaticPageNameOnApply, {
        value
      });
      biLogger.taskElementCtaClick({
        ctaName: BiCtaNames.Apply,
        index: 0,
        status: uiStatus
      });
      biLogger.taskElementApplyClick({
        index: 0,
        status: uiStatus,
        value
      });
      try {
        await setTitleTag(httpClient, errorHandler, itemType, value, routerId ?? pageId);
      } catch (e) {
        reportErrorPanorama(e, PanoramaErrorNames.updatePageName);
        showSaveErrorModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          errorMessage: errorHandler.getResolvedError(e).message,
          handleTryAgain: () => handleClickApply(value),
          t
        });
        throw e;
      }
      const newCheckerResult = ruleChecker({
        title: value
      });
      updateRuleStateAndVerifyIfNeeded(routerId ?? pageId, ruleId, {
        failedReasons: newCheckerResult.failedReasons,
        pageTitle: value,
        status: convertScanStatusToUiStatus(newCheckerResult.status)
      }, undefined, {
        skipVerification: true
      });
      setIsApplyTriggered(true);
      setRuleApplyTriggered({
        pageId,
        ruleId: RuleId.VALID_STATIC_PAGE_TITLE
      });
      finishPanoramaTransaction(PanoramaTransactions.validStaticPageNameOnApply, {
        value
      });
    };
    const getErrorMessage = failedReasons => {
      if (failedReasons && failedReasons.length > 0) {
        if ((failedReasons.includes(ValidPageTitleFailedReason.CopyOf) || failedReasons.includes(ValidPageTitleFailedReason.NewPage) || failedReasons.includes(ValidPageTitleFailedReason.Copy)) && failedReasons.includes(ValidPageTitleFailedReason.Length)) {
          return t('accessibility-wizard.rules-list.valid-title-tag.input-field.error-message.invalid-start-and-length', {
            maxLength
          });
        }
        if (failedReasons.includes(ValidPageTitleFailedReason.CopyOf) || failedReasons.includes(ValidPageTitleFailedReason.NewPage) || failedReasons.includes(ValidPageTitleFailedReason.Copy)) {
          return t('accessibility-wizard.rules-list.valid-title-tag.input-field.error-message.invalid-start');
        }
        if (failedReasons.includes(ValidPageTitleFailedReason.Length)) {
          return t('accessibility-wizard.rules-list.valid-title-tag.input-field.error-message.invalid-length', {
            maxLength
          });
        }
      }
      return undefined;
    };
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: getStatusBooleanValue(status) ? 'accessibility-wizard.rules-list.valid-title-tag.description' : 'accessibility-wizard.rules-list.valid-title-tag.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-page-title-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: 1,
            failedElementCount: Number(status === 'failed')
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.valid-static-page-title.description.learn-more.aria-label')
      }),
      fixer: FixerType.InputRule,
      fixerProps: {
        autoFocus: true,
        autoSelect: false,
        completedRuleContent: {
          editButtonLabel: t('accessibility-wizard.common.edit'),
          inputTitle: t('accessibility-wizard.rules-list.valid-title-tag.input-title'),
          title: t('accessibility-wizard.rules-list.issue-resolved'),
          value: isControlledInputFixerEnabled ? currentInputValue : initialTitle
        },
        fieldLabel: t('accessibility-wizard.rules-list.valid-title-tag.form-label'),
        footerLabels: {
          buttonTypes: getFooterButtonTypesFromCapabilities(capabilities),
          dismiss: t('accessibility-wizard.rules-list.valid-static-page-title.footer-button.cancel'),
          submit: t('accessibility-wizard.rules-list.valid-static-page-title.footer-button.apply')
        },
        ...(isControlledInputFixerEnabled ? {
          onChange: value => {
            setInputValues(prev => ({
              ...prev,
              [pageId]: value
            }));
          },
          value: currentInputValue
        } : {
          initialValue: initialTitle
        }),
        design: getFixerDesignPropsFromCapabilities(capabilities),
        maxInputLength: maxLength,
        onApply: handleClickApply,
        onBlur: value => {
          if (value !== title) {
            biLogger.elementFieldChange({
              index: 0,
              status: uiStatus,
              value
            });
          }
        },
        onCancel: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Cancel,
            index: 0,
            status: uiStatus
          });
          if (isControlledInputFixerEnabled) {
            setInputValues(prev => ({
              ...prev,
              [pageId]: initialTitle
            }));
          }
        },
        onEdit: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Edit,
            index: 0,
            status: uiStatus
          });
        },
        placeholder: isPlaceholderEnabled || capabilities.compactPanelChrome ? t('accessibility-wizard.rules-list.valid-title-tag.input-placeholder') : undefined,
        validator: value => {
          const {
            status: statusAfterValidation,
            failedReasons
          } = ruleChecker({
            title: value
          }, {
            isExperimentEnabled: spec => experiments.enabled(spec)
          });
          return !getStatusBooleanValue(statusAfterValidation) ? {
            status: 'error',
            statusMessage: getErrorMessage(failedReasons)
          } : undefined;
        }
      },
      id: `${routerId ?? pageId}-${ruleId}`,
      isEditableOnceCompleted: !isThankYouPage,
      label: t('accessibility-wizard.rules-list.valid-title-tag.title'),
      onRuleToggle: function (toggleType, index, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: index,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(itemType, isCrossPage),
            status: uiStatus,
            task_index: index,
            task_name: ruleId
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
        }
      },
      onStatusChange: () => {
        biLogger.taskStatusChange({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          status: uiStatus
        });
        biLogger.elementStatusChange({
          index: 0,
          status: uiStatus,
          value: initialTitle
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus,
      tips: /*#__PURE__*/React.createElement(RuleTipBox, null, /*#__PURE__*/React.createElement(RuleTipItem, {
        showIcon: capabilities.compactPanelChrome
      }, tip))
    };
  }, [getRuleState, itemType, inputValues, isControlledInputFixerEnabled, getGroupData, t, capabilities, editorHost, isPlaceholderEnabled, tip, startPanoramaTransaction, biLogger, updateRuleStateAndVerifyIfNeeded, setIsApplyTriggered, setRuleApplyTriggered, finishPanoramaTransaction, httpClient, errorHandler, reportErrorPanorama, a11yWizardAdapter, openSelectedArticle, experiments, setCurrentTaskInfo, currentTaskInfo, navigateToPage]);
  return {
    getValidStaticPageTitleProps
  };
};
//# sourceMappingURL=use-valid-static-page-title-rule.js.map